import { useEffect, useMemo, useRef, useState } from "react";
import type { RefObject } from "react";
import { useTranslation } from "react-i18next";
import { RefreshIcon } from "@heroicons/react/outline";
import { WorkflowOutputSuggestion } from "./interface";
import { postData, putData } from "./backend";
import { showToast } from "./toast";
import SuggestionCard from "./SuggestionCard";

type SuggestionRow = {
  suggestion: WorkflowOutputSuggestion;
  index: number;
  focusKey: string;
};

function getFocusKeyForSuggestion(suggestion: WorkflowOutputSuggestion, index: number): string {
  return `${suggestion.id || "__missing_id__"}::${index}`;
}

function getFirstPendingFocusKey(list: SuggestionRow[]): string | null {
  const next = list.find(row => row.suggestion.status === "PENDING");
  return next ? next.focusKey : null;
}

function getNextPendingFocusKeyAfterIndex(list: SuggestionRow[], index: number): string | null {
  for (let i = index + 1; i < list.length; i += 1) {
    if (list[i]?.suggestion.status === "PENDING")
      return list[i].focusKey;
  }
  return null;
}

function getErrorMessage(error: unknown): string {
  const message = error instanceof Error ? error.message : String(error);
  // Backend debug pages can return full HTML; avoid dumping that into toast/state.
  if (message.includes("<!DOCTYPE html>"))
    return "Sorry, something went wrong.";
  return message;
}

interface Props {
  token: string;
  outputId: string;
  suggestions: WorkflowOutputSuggestion[];
  readOnly?: boolean;
  isGenerating?: boolean;
  scrollContainerRef?: RefObject<HTMLDivElement | null>;
  onSuggestionsChange: (suggestions: WorkflowOutputSuggestion[]) => void;
  onNavigate: (navigationKey: string) => void;
}

function OutputSuggestionsPanel(props: Props) {
  const { token, suggestions, readOnly, isGenerating, scrollContainerRef, onSuggestionsChange, onNavigate } = props;
  const { t } = useTranslation();

  const [pendingFocusKey, setPendingFocusKey] = useState<string | null>(null);
  const [focusedSuggestionId, setFocusedSuggestionId] = useState<string | null>(null);
  const [lastActionContextByFocusKey, setLastActionContextByFocusKey] = useState<Record<string, {
    action: "apply" | "dismiss" | "unapply" | "restore";
    focusAfterSuccess: string | null;
  }>>({});
  const hasInitializedFocusRef = useRef(false);
  const cardElementByIdRef = useRef<Record<string, HTMLDivElement | null>>({});
  const lastNavigatedKeyRef = useRef<string | null>(null);

  const suggestionRows = useMemo(
    () => suggestions.map((suggestion, index) => ({
      suggestion,
      index,
      focusKey: getFocusKeyForSuggestion(suggestion, index),
    })),
    [suggestions],
  );

  const suggestionsByFocusKey = useMemo(
    () => new Map(suggestionRows.map(row => [row.focusKey, row.suggestion])),
    [suggestionRows],
  );

  function mergeUpdatedSuggestion(
    previous: WorkflowOutputSuggestion,
    updated: WorkflowOutputSuggestion,
  ): WorkflowOutputSuggestion {
    return {
      ...previous,
      ...updated,
      content: updated.content ?? previous.content,
      navigationKey: updated.navigationKey || previous.navigationKey,
      hasReverseActions: updated.hasReverseActions ?? previous.hasReverseActions,
    };
  }

  function getElementTopWithinContainer(element: HTMLElement, container: HTMLElement): number {
    const elementRect = element.getBoundingClientRect();
    const containerRect = container.getBoundingClientRect();
    return elementRect.top - containerRect.top + container.scrollTop;
  }

  useEffect(() => {
    if (!hasInitializedFocusRef.current) {
      hasInitializedFocusRef.current = true;
      setFocusedSuggestionId(getFirstPendingFocusKey(suggestionRows));
      return;
    }

    if (!focusedSuggestionId) {
      const firstPendingId = getFirstPendingFocusKey(suggestionRows);
      if (firstPendingId)
        setFocusedSuggestionId(firstPendingId);
      return;
    }

    const focusedSuggestion = suggestionsByFocusKey.get(focusedSuggestionId);
    if (!focusedSuggestion || focusedSuggestion.status !== "PENDING")
      setFocusedSuggestionId(getFirstPendingFocusKey(suggestionRows));
  }, [focusedSuggestionId, suggestionsByFocusKey, suggestionRows]);

  useEffect(() => {
    if (!focusedSuggestionId)
      return;

    const container = scrollContainerRef?.current;
    const focusedCard = cardElementByIdRef.current[focusedSuggestionId];
    if (container && focusedCard) {
      const focusedTop = getElementTopWithinContainer(focusedCard, container);
      container.scrollTo({ top: Math.max(0, focusedTop - 8), behavior: "smooth" });
    }
  }, [focusedSuggestionId, suggestions, scrollContainerRef]);

  useEffect(() => {
    if (!focusedSuggestionId)
      return;

    const focusedSuggestion = suggestionsByFocusKey.get(focusedSuggestionId);
    const navigationKey = focusedSuggestion?.navigationKey;
    if (!navigationKey)
      return;

    if (lastNavigatedKeyRef.current === navigationKey)
      return;

    lastNavigatedKeyRef.current = navigationKey;
    onNavigate(navigationKey);
  }, [focusedSuggestionId, suggestionsByFocusKey, onNavigate]);

  const performSuggestionAction = (
    suggestion: WorkflowOutputSuggestion,
    actionFocusKey: string,
    action: "apply" | "dismiss" | "unapply" | "restore",
    options?: { focusAfterSuccess?: string | null },
  ) => {
    if (readOnly)
      return;

    setPendingFocusKey(actionFocusKey);
    const focusAfterSuccess = options && "focusAfterSuccess" in options
      ? options.focusAfterSuccess ?? null
      : null;
    setLastActionContextByFocusKey(prev => ({
      ...prev,
      [actionFocusKey]: { action, focusAfterSuccess },
    }));

    postData(`/hubspot/app/output-suggestions/${suggestion.id}/${action}/`, token)
      .then((updated: WorkflowOutputSuggestion) => {
        onSuggestionsChange(suggestions.map((x, i) => (
          getFocusKeyForSuggestion(x, i) === actionFocusKey
            ? mergeUpdatedSuggestion(x, updated)
            : x
        )));
        if (options && "focusAfterSuccess" in options)
          setFocusedSuggestionId(focusAfterSuccess);
      })
      .catch((err: unknown) => {
        const errorMessage = getErrorMessage(err);
        showToast(errorMessage);
        onSuggestionsChange(suggestions.map((x, i) => (
          getFocusKeyForSuggestion(x, i) === actionFocusKey
            ? { ...x, status: "ERROR", error: errorMessage }
            : x
        )));
      })
      .finally(() => setPendingFocusKey(null));
  };

  const handleApply = (suggestion: WorkflowOutputSuggestion, index: number) => {
    const actionFocusKey = getFocusKeyForSuggestion(suggestion, index);
    const nextFocusedId = getNextPendingFocusKeyAfterIndex(suggestionRows, index);
    performSuggestionAction(suggestion, actionFocusKey, "apply", { focusAfterSuccess: nextFocusedId });
  };

  const handleDismiss = (suggestion: WorkflowOutputSuggestion, index: number) => {
    const actionFocusKey = getFocusKeyForSuggestion(suggestion, index);
    const nextFocusedId = getNextPendingFocusKeyAfterIndex(suggestionRows, index);
    performSuggestionAction(suggestion, actionFocusKey, "dismiss", { focusAfterSuccess: nextFocusedId });
  };

  const handleUndo = (suggestion: WorkflowOutputSuggestion, index: number) => {
    const actionFocusKey = getFocusKeyForSuggestion(suggestion, index);
    if (suggestion.status === "APPLIED") {
      if (!suggestion.hasReverseActions)
        return;
      performSuggestionAction(suggestion, actionFocusKey, "unapply", {
        focusAfterSuccess: actionFocusKey,
      });
      return;
    }
    if (suggestion.status === "DISMISSED") {
      performSuggestionAction(suggestion, actionFocusKey, "restore", {
        focusAfterSuccess: actionFocusKey,
      });
    }
  };

  const handleRetry = (suggestion: WorkflowOutputSuggestion, index: number) => {
    const actionFocusKey = getFocusKeyForSuggestion(suggestion, index);
    const actionContext = lastActionContextByFocusKey[actionFocusKey];
    if (!actionContext) return;
    performSuggestionAction(suggestion, actionFocusKey, actionContext.action, {
      focusAfterSuccess: actionContext.focusAfterSuccess,
    });
  };

  const handleSubmitFeedback = async (
    suggestion: WorkflowOutputSuggestion,
    index: number,
    feedbackSignal: "POSITIVE" | "NEGATIVE",
    feedbackMessage: string,
  ): Promise<boolean> => {
    if (readOnly)
      return false;

    const actionFocusKey = getFocusKeyForSuggestion(suggestion, index);
    setPendingFocusKey(actionFocusKey);
    try {
      const trimmedFeedbackMessage = feedbackMessage.trim();
      const feedbackPayload = {
        feedbackSignal,
        ...(trimmedFeedbackMessage ? { feedbackMessage: trimmedFeedbackMessage } : {}),
      };
      const updated: WorkflowOutputSuggestion = await putData(
        `/hubspot/app/output-suggestions/${suggestion.id}/`,
        token,
        feedbackPayload,
      );
      onSuggestionsChange(suggestions.map((x, i) => (
        getFocusKeyForSuggestion(x, i) === actionFocusKey
          ? mergeUpdatedSuggestion(x, updated)
          : x
      )));
      return true;
    } catch (err) {
      showToast(getErrorMessage(err));
      return false;
    } finally {
      setPendingFocusKey(null);
    }
  };

  const handleFocus = (suggestion: WorkflowOutputSuggestion, index: number) => {
    if (suggestion.status !== "PENDING" || readOnly) return;
    setFocusedSuggestionId(getFocusKeyForSuggestion(suggestion, index));
  };

  const hasSuggestions = suggestions.length > 0;

  return (
    <div className={`w-full ${hasSuggestions ? "min-h-[220px]" : ""}`}>
      {!hasSuggestions && (
        isGenerating
          ? (
            <div className="flex flex-col items-center gap-3 py-8 text-sm text-gray-500">
              <RefreshIcon className="w-6 h-6 animate-spin text-[#1875FB]" />
              <p className="text-center text-balance">{t("Analyzing document... this can take a few seconds.")}</p>
            </div>
          )
          : (
            <div className="py-4 text-sm text-gray-500">
              {!readOnly
                ? t("No suggestions available.")
                : t("No suggestions for this document.")}
            </div>
          )
      )}
      <div className="flex flex-col gap-3">
        {suggestionRows.map(({ suggestion, index, focusKey }) => (
          <SuggestionCard
            key={focusKey}
            cardRef={(node) => {
              cardElementByIdRef.current[focusKey] = node;
            }}
            suggestion={suggestion}
            index={index}
            readOnly={readOnly}
            isFocused={focusedSuggestionId === focusKey}
            isActionPending={pendingFocusKey === focusKey}
            canRetry={!!lastActionContextByFocusKey[focusKey]}
            onFocus={() => handleFocus(suggestion, index)}
            onApply={() => handleApply(suggestion, index)}
            onDismiss={() => handleDismiss(suggestion, index)}
            onUndo={() => handleUndo(suggestion, index)}
            onRetry={() => handleRetry(suggestion, index)}
            onSubmitFeedback={(feedbackSignal, feedbackMessage) =>
              handleSubmitFeedback(suggestion, index, feedbackSignal, feedbackMessage)
            }
          />
        ))}
      </div>
      {hasSuggestions && (
        <div className="mt-4 flex flex-col items-center gap-2">
          <p className="text-xs font-gilroy font-medium text-gray-500 text-center">
            {t("This feature is currently still in beta, and may be a paid feature in future.")}
          </p>
        </div>
      )}
    </div>
  );
}

export default OutputSuggestionsPanel;
